/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import HeaderInfo from "../components/common/HeaderInfo";
import toast from "react-hot-toast";
import { Delete, Edit, LocationIcon, Phone } from "../components/icons";
import DataTable from "../components/common/DataTable";

import VendorModal from "../components/modals/VendorModal";
import {
  useCreateVendorMutation,
  useGetAllVendorsQuery,
  useUpdateVendorMutation,
} from "../store/api";

export default function Vendors() {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [selectedVendor, setSelectedVendor] = useState(null);
  
  
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const { data, isLoading, isFetching, error } = useGetAllVendorsQuery({
    page,
    search: debouncedSearch,
  });

  useEffect(() => {
    if (error) {
      toast.error(error?.data?.message || "Failed to load vendors");
    }
  }, [error]);

  const vendors = data?.data?.vendors || [];
  
  const backendTotalPages = data?.data?.totalPages || 1;
  let totalPages = backendTotalPages;

  function handleNext() {
    if (page < totalPages) setPage((p) => p + 1);
  }

  function handlePrev() {
    if (page > 1) setPage((p) => p - 1);
  }

  const [createVendor, { isLoading: isCreatingVendor }] =
    useCreateVendorMutation();

  const [updateVendor, { isLoading: isUpdatingVendor }] =
    useUpdateVendorMutation();

  function handleOpenEdit(row) {
    setSelectedVendor(row);
    setModalMode("edit");
    setModalOpen(true);
  }

  function handleCloseModal() {
    setModalOpen(false);
    setSelectedVendor(null);
    setModalMode("add");
  }

  function handleOpenAdd() {
    setSelectedVendor(null);
    setModalMode("add");
    setModalOpen(true);
  }



  let rows = vendors;

  // Fallback to frontend pagination ONLY if backend doesn't paginate correctly at all
  if (!data?.data?.totalPages && vendors.length > 0) {
    totalPages = Math.ceil(vendors.length / 20) || 1;
    rows = vendors.slice((page - 1) * 20, page * 20);
  }

  const renderRow = (r, idx) => (
    <div
      key={r._id}
      className="bg-[#EEEEEE40]"
    >
      <div className="grid grid-cols-12 gap-4 px-7 py-6 items-center border-b border-[#E5E5E5]">
        <div className="col-span-3 flex items-center gap-[14px] text-black">
          <div className="w-[53px] h-[42px] flex justify-center items-center rounded bg-[#E4E3E5]">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13 2L2 7M12 3V22H7C5.114 22 4.172 22 3.586 21.414C3 20.828 3 19.886 3 18V7M12 7L22 12"
                stroke="black"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M10 22H17C18.886 22 19.828 22 20.414 21.414C21 20.828 21 19.885 21 18V11.5M18 10V7M7 11H8M7 15H8M16 14H17M16.5 22V18"
                stroke="black"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div>
            <p className="text-sm  leading-5">{r.name}</p>
          </div>
        </div>

        <div className="col-span-3">
          <div className="text-black flex items-center justify-center">
            <Phone />
            <p>{r.phoneNumber}</p>
          </div>
          <p className="text-[10px] ml-4 text-center leading-4 text-[#878686]">
            Primary Business Contact
          </p>
        </div>

        <div className="col-span-3">
          <div className="text-black flex items-center justify-center text-sm">
            {r.email ? (
              <p>{r.email}</p>
            ) : (
              <span className="text-gray-400 italic">Not Added</span>
            )}
          </div>
        </div>

        <div className="col-span-2">
          <div className="text-xs leading-4 text-black flex items-start gap-1.5" title={r.address}>
            <div className="flex-shrink-0 mt-0.5">
              <LocationIcon color="#000" size="20" />
            </div>
            <p className="line-clamp-2 break-words">{r.address}</p>
          </div>
        </div>

        <div className="col-span-1 flex justify-end gap-3">
          <button aria-label="edit" onClick={() => handleOpenEdit(r)}>
            <Edit />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="pt-6 md:pt-14 px-4 md:pl-8 md:pr-12 min-h-screen">
      <HeaderInfo
        title="Global Vendors"
        desc="Maintain strategic partnerships and fulfillment channels"
        btnLabel="On board New Vendor"
        isSearchBar={true}
        onAdd={handleOpenAdd}
        searchPlaceholder="Query by supplier name or address"
        searchValue={search}
        onSearchChange={setSearch}
        showFilterButton={false}
      />
      <div className="mt-6 ml-0 md:ml-2.5">
        <DataTable
          columns={[
            {
              key: "vendors",
              label: "Vendor Details",
              className: "col-span-3",
            },
            {
              key: "contact",
              label: "Contact Number",
              className: "col-span-3 text-center",
            },
            {
              key: "email",
              label: "Email",
              className: "col-span-3 text-center",
            },
            {
              key: "address",
              label: "Fulfillment Address",
              className: "col-span-2",
            },
            { key: "actions", label: "Actions", className: "col-span-1 text-right" },
          ]}
          rows={rows}
          renderRow={renderRow}
          page={page}
          totalPages={totalPages}
          onNext={handleNext}
          onPrev={handlePrev}
          isLoading={isLoading || isFetching}
        />
      </div>

      <VendorModal
        open={modalOpen}
        mode={modalMode}
        initialValues={selectedVendor}
        onClose={handleCloseModal}
        isLoading={modalMode === "add" ? isCreatingVendor : isUpdatingVendor}
        onSubmit={async (val) => {
          try {
            if (modalMode === "add") {
              const payload = {
                name: val.name,
                phoneNumber: val.phoneNumber,
                address: val.address,
              };
              if (val.email?.trim()) payload.email = val.email.trim();
              
              const response = await createVendor(payload).unwrap();
              if (response?.success === false) {
                toast.error(response?.message || "Failed to create vendor");
                throw new Error(response?.message || "Failed to create vendor");
              }
              toast.success("Vendor created successfully");
            } else {
              const payload = {
                id: selectedVendor._id,
                name: val.name,
                phoneNumber: val.phoneNumber,
                address: val.address,
                email: val.email?.trim() || "",
              };

              const response = await updateVendor(payload).unwrap();
              if (response?.success === false) {
                toast.error(response?.message || "Failed to update vendor");
                throw new Error(response?.message || "Failed to update vendor");
              }
              toast.success("Vendor updated successfully");
            }
            handleCloseModal();
          } catch (error) {
            console.error(error);
            toast.error(error?.data?.message || "Operation failed");
            throw error;
          }
        }}
      />
    </div>
  );
}
